// One locale in, four answers out: currency, date format, distance unit, and
// the day the week begins on.
//
// ── Why this is derived and not stored ─────────────────────────────────────
//
// Currency is not a setting in MMS: Business Settings → Accounts & Invoices has
// no currency field. It is decided by the Region dropdown in the Edit Studio
// Profile drawer — a full `Country - Language` culture list — whose hint reads:
//
//     Your selection controls currency, mileage log format, and date/time format
//
// So one choice drives three things, and storing them apart would let them
// drift (a United Kingdom studio showing `$`, or `MM/DD/YYYY`). There is one
// stored field, `settings.locale`, and everything else is a function of it.
//
// ── The seam ───────────────────────────────────────────────────────────────
//
// This module is pure: no React, no Firebase, no clock. It never reads the
// setting. `settings.locale` is stored and exposed by src/data/useSettings.jsx
// (its counterpart — that file owns persistence, this one owns meaning), and
// src/context/LocaleContext.jsx is where the two meet for the UI.
//
// ── Why currency is a table and the date format is not ─────────────────────
//
// `Intl` has no locale→currency mapping — the association is a fact about
// countries, not about language data — so that half is a table of constants,
// the same posture as EXPONENTS in ./money.js: extend it rather than invent an
// algorithm. The date format is the opposite case: `Intl.DateTimeFormat` *is*
// the CLDR short-date pattern, so it is derived from there (en-CA is
// `2026-09-12`, de-DE is `12.09.2026`, ja-JP is `2026/09/12`).

// `./core.js`, not `../lib/core.js`: this file is copied verbatim into
// functions/domain/, and that door is inside the copy (#218).
import { coreSync } from './core.js';
import { ldmlFromParts } from './datePattern.js';
import { format, formatInParentheses } from './money.js';

// A locale identifier, when nothing has been chosen or what was chosen is not
// a locale at all. Stated rather than implied, and exported so a caller can say
// "this is the fallback" in the UI rather than presenting a guess as a choice.
export const FALLBACK_LOCALE = 'en-US';

// The currency an unknown region falls back to.
//
// Getting a currency wrong is a hundredfold error on a document someone files
// with an accountant, so the rule here is: never invent one. An unrecognised
// region gets USD *and says so* — `deriveLocale()` returns currencyIsFallback,
// so a caller can show "USD (not set for your region)" instead of pretending.
export const FALLBACK_CURRENCY = 'USD';

// Region -> ISO 4217, for the regions a music studio plausibly bills in.
//
// Coverage is partial: North America, Europe, the Anglophone and East Asian
// markets, the larger Latin American and Middle Eastern ones. An unlisted
// region takes FALLBACK_CURRENCY and is flagged, rather than being matched to
// something plausible-looking.
//
// Every currency here that is not two-decimal must also appear in `EXPONENTS`
// in core/src/money.rs, which the web, the phones and the report PDF service
// all ask; a currency known here but not there would be formatted with the
// wrong number of decimal places, which is the hundredfold error again.
// test/domain/reportPdfMoney.test.js checks every currency here against
// ISO 4217's minor units as Intl carries them, so a forgotten one fails.
//
// **The table the rules read is `locale::CURRENCIES` in the core** —
// `currencyFor` and `deriveLocale` ask it, and so do the phones. This copy
// exists because `KNOWN_REGIONS` below is read at module scope, before the core
// can have been loaded, and it is exported only so
// `test/domain/peopleCore.test.js` can hold it to the core's, row for row. Add
// a region in both.
export const CURRENCY_BY_REGION = {
  // North America
  US: 'USD', CA: 'CAD', MX: 'MXN', PR: 'USD',

  // Euro area
  AT: 'EUR', BE: 'EUR', CY: 'EUR', DE: 'EUR', EE: 'EUR', ES: 'EUR', FI: 'EUR',
  FR: 'EUR', GR: 'EUR', HR: 'EUR', IE: 'EUR', IT: 'EUR', LT: 'EUR', LU: 'EUR',
  LV: 'EUR', MT: 'EUR', NL: 'EUR', PT: 'EUR', SI: 'EUR', SK: 'EUR',

  // Rest of Europe
  GB: 'GBP', CH: 'CHF', NO: 'NOK', SE: 'SEK', DK: 'DKK', IS: 'ISK',
  PL: 'PLN', CZ: 'CZK', HU: 'HUF', RO: 'RON', BG: 'BGN', RS: 'RSD',
  UA: 'UAH', TR: 'TRY', RU: 'RUB',

  // Asia-Pacific
  AU: 'AUD', NZ: 'NZD', JP: 'JPY', KR: 'KRW', CN: 'CNY', TW: 'TWD',
  HK: 'HKD', SG: 'SGD', MY: 'MYR', TH: 'THB', ID: 'IDR', PH: 'PHP',
  VN: 'VND', IN: 'INR', PK: 'PKR', BD: 'BDT', LK: 'LKR',

  // Middle East and Africa
  AE: 'AED', SA: 'SAR', QA: 'QAR', KW: 'KWD', BH: 'BHD', OM: 'OMR',
  JO: 'JOD', IL: 'ILS', EG: 'EGP', ZA: 'ZAR', NG: 'NGN', KE: 'KES',
  GH: 'GHS', MA: 'MAD', TN: 'TND', DZ: 'DZD',

  // Latin America
  BR: 'BRL', AR: 'ARS', CL: 'CLP', CO: 'COP', PE: 'PEN', UY: 'UYU',
  CR: 'CRC', DO: 'DOP', GT: 'GTQ', PA: 'PAB', EC: 'USD',
};

// Where the mileage log is kept in miles rather than kilometres.
//
// The same MMS setting drives it ("currency, mileage log format, and date/time
// format"), so it belongs here rather than in a mileage module. `Intl` does not
// expose a measurement system, so this is a table too. Anything not listed
// gets kilometres.
//
// The table is `MILES_REGIONS` in `core/src/expenses.rs`, beside the mileage
// log's other rules, so a phone keeps its log in the same unit as the web;
// `distanceUnitFor` below asks it.
export const DISTANCE_UNITS = { miles: 'mi', kilometres: 'km' };

// The region subtag of a locale identifier, or null.
//
// Accepts `en-US`, `en_US`, `zh-Hant-TW`, `en-US-u-ca-gregory`. Tolerant of
// the underscore form and of case: locale strings arrive from a
// dropdown, a stored document and a browser, and all three spell it differently.
// It stops at a `-u-`/`-x-` extension, whose subtags can look like regions, and
// answers a UN M.49 numeric region ("es-419") as itself — real, and never in
// our table, so it resolves to the stated fallback rather than being mistaken
// for a script.
//
// **This and `currencyFor` are `core/src/locale.rs`'s**: a phone parses a
// typed rate in the studio's currency, and must derive the same currency from
// the same locale as the web.
export function regionOf(locale) {
  return coreSync().regionOf(String(locale || '')) ?? null;
}

export function currencyFor(locale) {
  return coreSync().currencyFor(String(locale || ''));
}

// The regions this app claims to know about, in the one place the claim is
// made. Exported so ./phone.js can offer a calling code for each of them and a
// test can assert the two lists agree: no region we bill in without a phone
// number entry, or the other way round.
export const KNOWN_REGIONS = Object.freeze(Object.keys(CURRENCY_BY_REGION));

export function distanceUnitFor(locale) {
  return coreSync().expenseDistanceUnit(String(locale || ''));
}

// A locale identifier `Intl` will accept, or the fallback.
//
// A garbage value must not throw where a date is being drawn. `Intl` raises
// RangeError on a malformed tag, so it is asked once here and the answer reused.
export function normaliseLocale(locale) {
  return resolveLocale(locale).locale;
}

// Whether the locale we are using is the one that was asked for.
//
// Kept separate from "did we know the region" because they are different
// failures with the same symptom: a stored value that is not a locale at all,
// versus a real locale whose region is not in the table. Both end at USD, and
// deriveLocale() flags both, so nothing presents the fallback as an answer.
//
// **Which tag is a locale is the core's** (`resolve_locale` in
// core/src/locale.rs). `Intl.DateTimeFormat.supportedLocalesOf` throws on a
// structurally invalid tag, while the phones' own parsers never throw — so,
// asked of each platform, a stored value the web writes in en-US a phone
// writes in the root locale. The core makes the structural test Intl makes,
// recorded against Node's (`core/src/locale_vectors.json`), and every client
// asks it. A well-formed tag with no data is still accepted, and `Intl` falls
// back to the default locale's data for it.
function resolveLocale(locale) {
  const tag = coreSync().resolveLocale(String(locale || ''));
  return tag ? { locale: tag, isFallback: false } : { locale: FALLBACK_LOCALE, isFallback: true };
}

// 2026-01-02, asked of Intl to find out two things: which order this locale
// puts year, month and day in, and whether it pads them.
//
// **What is asked is the locale's CLDR short date** (`dateStyle: 'short'`),
// its year then written in full (#537). Not the `yMd` skeleton (`year`,
// `month`, `day: 'numeric'`), which in German does not pad — `1.9.2026` —
// where MMS writes `01.09.2026`: .NET's short date, which on ICU is this same
// CLDR short format with its two-digit year widened (`short_date_pattern` in
// core/src/locale.rs). The United States (`9/1/2026`) and the United Kingdom
// (`01/09/2026`) read the same either way. Where MMS writes Windows' short
// date instead of CLDR's — Australia, India and the Netherlands (#551) — the
// core says so, and `shortDatePattern` hands it the locale for that reason.
//
// Single-digit month *and* day, so the width answers the padding question: a
// day of 12 is two characters whether the locale pads or not. The order is
// read off formatToParts' labels, not the values.
//
// This is the one `new Date` in the module and it is a probe, not a user's
// date: built on the UTC grid and read back with timeZone 'UTC' pinned, so no
// zone can shift it. Nothing a user typed is ever put through a Date here —
// see formatDateOnly.
const PROBE = new Date(Date.UTC(2026, 0, 2));

// Asking Intl is not free and a table redraws hundreds of dates, so the layout
// is worked out once per locale. The answer is a property of the locale alone —
// no clock, no data — so caching it cannot go stale.
const partsCache = new Map();

function shortDateParts(locale) {
  const tag = normaliseLocale(locale);
  const hit = partsCache.get(tag);
  if (hit) return hit;
  let parts;
  try {
    parts = new Intl.DateTimeFormat(tag, { timeZone: 'UTC', dateStyle: 'short' }).formatToParts(PROBE);
  } catch {
    parts = new Intl.DateTimeFormat(FALLBACK_LOCALE, { timeZone: 'UTC', dateStyle: 'short' }).formatToParts(PROBE);
  }
  partsCache.set(tag, parts);
  return parts;
}

const TOKENS = { year: 'Y', month: 'M', day: 'D' };

// The date pattern this locale writes, as a readable string: `M/D/YYYY` for the
// United States (which is what MMS shows a US studio), `DD/MM/YYYY` for the
// UK, `YYYY-MM-DD` for Canada, `DD.MM.YYYY` for Germany, `D/MM/YYYY` for
// Australia. The year is always whole, as it is drawn, though the short format
// Intl laid out wrote two digits of it.
//
// For an empty date box, and for tests to assert against. Read off
// `shortDatePattern` — the pattern formatDateOnly draws into — field by field,
// so the two cannot disagree.
export function dateFormatFor(locale) {
  const pattern = shortDatePattern(locale);
  let out = '';
  let quoted = false;
  for (let i = 0; i < pattern.length; i += 1) {
    const c = pattern[i];
    if (c === "'") {
      if (pattern[i + 1] === "'") {
        out += "'";
        i += 1;
      } else {
        quoted = !quoted;
      }
    } else if (!quoted && TOKENS[FIELD_OF[c]]) {
      let n = 1;
      while (pattern[i + n] === c) n += 1;
      const token = TOKENS[FIELD_OF[c]];
      // Repeated to the width the pattern draws, so it is honest about
      // padding: en-CA is `YYYY-MM-DD`, not `YYYY-M-D`.
      out += token === 'Y' ? 'YYYY' : token.repeat(Math.min(2, n));
      i += n - 1;
    } else {
      out += c;
    }
  }
  return out.trim();
}

const FIELD_OF = { y: 'year', M: 'month', d: 'day' };

// The same layout as an LDML pattern — `M/d/y` in the US, `dd/MM/y` in the
// UK, `y-MM-dd` in Canada, `dd.MM.y` in Germany: what the core's `formatDate`
// draws a stored date into (core/src/locale.rs), and what the report PDF and
// the receipt scanner are handed so they write and read a date the way the
// screen does. Built by `ldmlFromParts` (./datePattern.js) from the same
// probe, so it is padded exactly as Intl rendered it: the short format
// gives an unpadded 1 in en-US and a padded 01 in en-CA, and that is the
// difference between 9/12/2026 and 2026-09-12. The core's
// `short_date_pattern` writes the year whole, and answers MMS's own pattern
// instead where MMS was read to write another (`d/MM/y` in Australia, #551).
const patternCache = new Map();
export function shortDatePattern(locale) {
  const tag = normaliseLocale(locale);
  if (!patternCache.has(tag)) {
    patternCache.set(tag, coreSync().shortDatePattern(tag, ldmlFromParts(shortDateParts(tag))));
  }
  return patternCache.get(tag);
}

// A date typed into a box, in the studio's short date — `30.09.2026` for
// Germany, `9/30/2026` for the United States, and the stored `2026-09-30` in
// any Region — as the stored date-only string, or '' (#526). The reading is
// the core's (`parse_typed_date`).
export function parseTypedDate(text, locale) {
  return coreSync().parseTypedDate(String(text ?? ''), shortDatePattern(locale)) ?? '';
}

// The character this locale writes between a number's whole and its
// fraction: `.` in the United States, `,` in Germany. A CLDR fact, asked of
// Intl, for the boxes that show a typed number in the Region (#526): the
// rule that reads one back is the core's `typed_decimal`.
const decimalCache = new Map();
export function decimalSeparatorFor(locale) {
  const tag = normaliseLocale(locale);
  if (!decimalCache.has(tag)) {
    let mark = '.';
    try {
      mark = new Intl.NumberFormat(tag).formatToParts(1.5).find((p) => p.type === 'decimal')?.value || '.';
    } catch { /* the point */ }
    decimalCache.set(tag, mark);
  }
  return decimalCache.get(tag);
}

// A number a box holds (`16.95`) as the Region shows it (`16,95`), and what
// is typed into one back to the held spelling — both the core's rules
// (`shown_decimal`, `typed_decimal`), so the three platforms' boxes agree.
export function shownDecimal(held, locale) {
  return coreSync().shownDecimal(String(held ?? ''), decimalSeparatorFor(locale));
}

// In a comma Region a typed point is a digit group, as in MMS (#551):
// `16.95` is 1695 in Germany.
export function typedDecimal(text, locale) {
  return coreSync().typedDecimal(String(text ?? ''), decimalSeparatorFor(locale));
}

// Whether the locale's clock runs to twelve, and its words for the two
// halves of the day — `AM` and `PM` in English, `오전` and `오후` in Korean.
// CLDR facts, so asked of Intl: what a typed time box offers and reads back
// (`parse_time` in the core takes the words).
const clockCache = new Map();
function clockOf(locale) {
  const tag = normaliseLocale(locale);
  if (!clockCache.has(tag)) {
    const period = (h) => {
      try {
        return new Intl.DateTimeFormat(tag, { hour: 'numeric', hourCycle: 'h12', timeZone: 'UTC' })
          .formatToParts(new Date(TIME_PROBE + h * 3600000))
          .find((p) => p.type === 'dayPeriod')?.value || '';
      } catch {
        return '';
      }
    };
    let cycle = 'h12';
    try { cycle = new Intl.DateTimeFormat(tag, { hour: 'numeric' }).resolvedOptions().hourCycle || 'h12'; } catch { /* en-US's */ }
    clockCache.set(tag, { am: period(1), pm: period(13), twelveHour: cycle === 'h11' || cycle === 'h12' });
  }
  return clockCache.get(tag);
}

export function usesTwelveHourClock(locale) {
  return clockOf(locale).twelveHour;
}

// A clock reading typed into a box — `16:00`, `4:00 PM`, `4pm` — as the
// stored `HH:MM`, or ''.
export function parseTypedTime(text, locale) {
  const { am, pm } = clockOf(locale);
  return coreSync().parseTime(String(text ?? ''), am || 'AM', pm || 'PM') ?? '';
}

// A date-only string -> the locale's short date.
//
// A date-only value constructed in local time lands a day early west of
// Greenwich, so this never constructs a Date from `iso`: the year, month and
// day are taken straight out of the string and dropped into the pattern.
// There is no instant and no zone.
//
// **The drawing is the core's** (`format_date`, #510), so the sentences the
// core builds around a date — Package Activity's, a report's Period — draw it
// exactly as this does, and so do both phones, handed the pattern their own
// ICU answers to the same `yMd` question. `core/src/date_vectors.json` holds
// the core to `substituteParts` in ./datePattern.js for every region's locale.
//
// Returns '' for anything that is not a date-only string, so callers keep their
// own placeholder ('–' in the expense list, '—' in the import preview).
export function formatDateOnly(iso, locale) {
  return coreSync().formatDate(String(iso ?? ''), shortDatePattern(locale)) ?? '';
}

const YEAR_MONTH = /^(\d{4})-(\d{2})$/;

// "16:00" -> the clock reading this locale uses. "4:00 PM" in en-US, "16:00" in
// de-DE and ja-JP.
//
// One function for every surface, so one lesson cannot read "16:00" on its
// chip and "4:00 PM" in the dialog about it. Whether a locale writes noon as
// 12 PM or 12:00 is a CLDR fact, so it is asked rather than guessed at — the
// same reasoning dateFormatFor uses.
//
// **The locale's CLDR short time** (`timeStyle: 'short'`), as the date is its
// short date (#537): MMS writes a German studio's morning `07:00` (#534) —
// .NET's short time, `HH:mm` — where `hour: 'numeric'` drops the pad
// (`7:00`). The United States reads `4:00 PM` either way, and Android's
// `FormatStyle.SHORT` asks the same.
//
// Formatted against a fixed UTC instant with timeZone: 'UTC', so the hours and
// minutes that come out are the ones that went in. Building a local Date from
// "16:00" and formatting it in the machine's zone would shift the clock.
//
// This function *writes* a clock reading; it does not move one. A lesson's
// "16:00" is wall-clock time in its series' own zone (domain/timeZone.js), and
// what comes back is that same reading in this locale's notation — 4:00 PM in
// en-US, 16:00 in de-DE. Reading it in some *other* zone is a separate step,
// and only the family-facing surfaces take it: see `timeReadings` there.
const TIME_PROBE = Date.UTC(2000, 0, 1);

export function formatTime(hhmm, locale) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || '').trim());
  if (!m) return String(hhmm || '');
  const hours = Number(m[1]);
  const minutes = Number(m[2]);
  if (hours > 23 || minutes > 59) return String(hhmm);
  try {
    return new Intl.DateTimeFormat(normaliseLocale(locale), {
      timeStyle: 'short', timeZone: 'UTC',
    }).format(new Date(TIME_PROBE + (hours * 60 + minutes) * 60000));
  } catch {
    return String(hhmm);
  }
}

// ── Calendar words in the Region's language (#534) ──────────────────────────
//
// MMS writes a Germany - German studio's calendar in German: the week view's
// title `Mittwoch, 30. September 2026`, its
// columns `Mo 28`, `Di 29`, the month view's headers `Mo Di Mi Do Fr Sa So`,
// a new month's first day `Okt` and `1`, and times on a 24-hour clock
// (`07:00`, `16:00 - 16:30`). Its own words — Month, Week, Today — stay
// English, as ours do. Each of these is a CLDR word, so it is Intl's in the
// studio's locale, drawn at noon UTC on the stored date's own day so no zone
// can move it. In English every one reads as date-fns' `EEE`, `EEEE`, `MMM`
// and `EEEE, MMMM d, yyyy` write it.
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

function dateWords(iso, locale, options) {
  const m = DATE_ONLY.exec(String(iso || ''));
  if (!m) return '';
  const at = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12));
  try {
    return new Intl.DateTimeFormat(normaliseLocale(locale), { ...options, timeZone: 'UTC' }).format(at);
  } catch {
    return new Intl.DateTimeFormat(FALLBACK_LOCALE, { ...options, timeZone: 'UTC' }).format(at);
  }
}

// "Wed" / "Mi": the day's short name, stand-alone, as a column header.
export function formatWeekdayShort(iso, locale) {
  return dateWords(iso, locale, { weekday: 'short' });
}

// "Wednesday" / "Mittwoch".
export function formatWeekdayLong(iso, locale) {
  return dateWords(iso, locale, { weekday: 'long' });
}

// The seven short day names, Sunday first: 2026-09-27 is a Sunday.
const A_WEEK = ['2026-09-27', '2026-09-28', '2026-09-29', '2026-09-30', '2026-10-01', '2026-10-02', '2026-10-03'];
export function weekdayNames(locale) {
  return A_WEEK.map((iso) => formatWeekdayShort(iso, locale));
}

// "Oct" / "Okt": a month's short name, stand-alone, for the first day of a
// month in the month view.
export function formatMonthShort(iso, locale) {
  return dateWords(iso, locale, { month: 'short' });
}

// The twelve short month names, January first: what the Month grid writes
// before a month's 1st, handed to the core as the phones hand theirs (#748).
const A_YEAR = Array.from({ length: 12 }, (_, i) => `2026-${String(i + 1).padStart(2, '0')}-01`);
export function monthShortNames(locale) {
  return A_YEAR.map((iso) => formatMonthShort(iso, locale));
}

// "Wednesday, September 30, 2026" / "Mittwoch, 30. September 2026": the
// locale's full date, the day and week views' title.
//
// MMS titles its day and week views with Windows' long-date table rather
// than CLDR's full date, so three Regions read differently there: the United
// Kingdom pads the day (`Friday, 02 October 2026`), and India
// (`02 October 2026`) and Japan (`2026年10月2日`) drop the weekday. We
// knowingly keep the platform's own full date (#594); the phones'
// `longDate` does the same.
export function formatLongDate(iso, locale) {
  return dateWords(iso, locale, { dateStyle: 'full' });
}

// An hour on the week view's time axis: "7 AM" on a twelve-hour clock, as
// date-fns' `h a` writes it, and the Region's full reading, "07:00", on a
// twenty-four-hour one (MMS's German axis).
export function formatHour(hour, locale) {
  const h = Number(hour);
  const reading = `${String(h).padStart(2, '0')}:00`;
  if (!usesTwelveHourClock(locale)) return formatTime(reading, locale);
  try {
    return new Intl.DateTimeFormat(normaliseLocale(locale), { hour: 'numeric', timeZone: 'UTC' })
      .format(new Date(TIME_PROBE + h * 3600000));
  } catch {
    return formatTime(reading, locale);
  }
}

// A clock reading split into the time and its day period — `{ time: '4:00',
// period: 'PM' }` in English, `{ time: '16:00', period: '' }` in German — for
// a row that sets the two in different type.
export function clockParts(hhmm, locale) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || '').trim());
  if (!m || Number(m[1]) > 23 || Number(m[2]) > 59) return { time: String(hhmm || ''), period: '' };
  try {
    const parts = new Intl.DateTimeFormat(normaliseLocale(locale), { timeStyle: 'short', timeZone: 'UTC' })
      .formatToParts(new Date(TIME_PROBE + (Number(m[1]) * 60 + Number(m[2])) * 60000));
    const period = parts.filter((p) => p.type === 'dayPeriod').map((p) => p.value).join('');
    const time = parts.filter((p) => p.type !== 'dayPeriod').map((p) => p.value).join('').trim();
    return { time, period };
  } catch {
    return { time: String(hhmm), period: '' };
  }
}

// "2026-09" -> "September 2026", in the studio's locale.
//
// The month heading over the expense list and the ticks under the trend chart.
// Unlike formatDateOnly this needs a Date — a month's name cannot come out of
// a string — so it builds one on the UTC grid and pins timeZone to UTC when
// reading it back, which keeps a September midnight from being drawn as August
// in California.
export function formatMonthYear(yearMonth, locale) {
  const m = YEAR_MONTH.exec(String(yearMonth || ''));
  if (!m) return '';
  const at = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 1));
  try {
    return new Intl.DateTimeFormat(normaliseLocale(locale), {
      timeZone: 'UTC', month: 'long', year: 'numeric',
    }).format(at);
  } catch {
    return new Intl.DateTimeFormat(FALLBACK_LOCALE, {
      timeZone: 'UTC', month: 'long', year: 'numeric',
    }).format(at);
  }
}

// Everything one locale decides, in one object.
//
// The shape the UI consumes, so a component asks once rather than calling
// each function with a locale. `currencyIsFallback` is true when the region was
// not in the table, so nothing presents FALLBACK_CURRENCY as a derived answer.
export function deriveLocale(locale) {
  const { locale: tag, isFallback } = resolveLocale(locale);
  const region = regionOf(tag);
  const regional = region ? (coreSync().currencyOfRegion(region) ?? null) : null;
  const known = !isFallback && Boolean(regional);
  return {
    locale: tag,
    region,
    currency: known ? regional : FALLBACK_CURRENCY,
    // True when the currency was not derived: either the stored value was not
    // a locale, or its region is outside CURRENCY_BY_REGION. Either way this is
    // FALLBACK_CURRENCY being stated, not a region's currency being known.
    currencyIsFallback: !known,
    localeIsFallback: isFallback,
    dateFormat: dateFormatFor(tag),
    // The same layout as the pattern the core draws dates into — what a
    // caller hands a core sentence that carries a date (#510).
    datePattern: shortDatePattern(tag),
    distanceUnit: distanceUnitFor(tag),
    // `.` or `,`: what a typed number's box shows between whole and
    // fraction (#526).
    decimalSeparator: decimalSeparatorFor(tag),
    twelveHourClock: usesTwelveHourClock(tag),
    // The day the calendar's week begins on, 0 Sunday .. 6 Saturday —
    // date-fns' `weekStartsOn`. MMS decides it by Region and nothing else
    // (United Kingdom begins on Monday, United States on Sunday, #505); the
    // rule is the core's `first_weekday`, so both phones' week strips begin
    // where this calendar does.
    weekStartsOn: coreSync().firstWeekday(tag),
  };
}

// ── What a studio's Region writes, bound once (#500) ───────────────────────
//
// `deriveLocale`, with its locale and currency already tied into the
// formatters. Two callers, making the same call:
//
//   * src/context/LocaleContext.jsx, which is every figure and date on the
//     studio's own screens;
//   * functions/delivery/templates.js, which is every email and text the
//     studio sends a family — handed the same stored `settings.locale` the
//     screen is handed (delivery/facts.js).
//
// So the email says what the screen says by being one function with one
// input: a family reads money and dates in the studio's Region (#500), the
// same as its screens, invoices and PDFs.
//
// `import { format }` from ./money.js is the one import this adds, and money.js
// imports nothing from here, so there is no cycle.
export function studioFormatters(locale) {
  const derived = deriveLocale(locale);
  return {
    ...derived,
    formatMoney: (minor) => format(minor, derived.currency, derived.locale),
    // A negative in parentheses, `($10.00)` (#649): the ledger's pills.
    formatMoneyInParentheses: (minor) => formatInParentheses(minor, derived.currency, derived.locale),
    formatDate: (iso) => formatDateOnly(iso, derived.locale),
    formatTime: (hhmm) => formatTime(hhmm, derived.locale),
    formatMonth: (yearMonth) => formatMonthYear(yearMonth, derived.locale),
    // The calendar's words (#534) and the typed boxes (#526), bound the same
    // way, so a component asks the context rather than a locale.
    formatWeekdayShort: (iso) => formatWeekdayShort(iso, derived.locale),
    formatWeekdayLong: (iso) => formatWeekdayLong(iso, derived.locale),
    formatMonthShort: (iso) => formatMonthShort(iso, derived.locale),
    formatLongDate: (iso) => formatLongDate(iso, derived.locale),
    formatHour: (hour) => formatHour(hour, derived.locale),
    clockParts: (hhmm) => clockParts(hhmm, derived.locale),
    weekdayNames: weekdayNames(derived.locale),
    monthShortNames: monthShortNames(derived.locale),
    parseDate: (text) => parseTypedDate(text, derived.locale),
    parseTime: (text) => parseTypedTime(text, derived.locale),
    shownDecimal: (held) => shownDecimal(held, derived.locale),
    typedDecimal: (text) => typedDecimal(text, derived.locale),
  };
}

// ── The Region a new studio starts with (#462) ─────────────────────────────
//
// `DEFAULT_LOCALE` in ./studio.js is `en-US`; a new studio takes the
// browser's answer instead (#462), asked **once, at sign-up**, and written to
// `settings/studio` as the studio's own value (`createStudioSettings` in
// src/data/useSettings.jsx).
//
// Once and stored, never at read time. A studio whose document has no locale
// is still `en-US` on every device, as `docs/features/multi-currency.md` §5
// requires: were an absent value read as *this browser's* region, the same
// studio would bill in pounds on the teacher's laptop and dollars on her phone.
// So this is a default for a document being created, not a fallback for one
// being read — and nothing on a phone calls it, because a phone cannot create a
// studio (it signs in with a code from one).
//
// Which tag, from `navigator.languages`:
//
//   * the first one, when it names a region — `en-GB`, `fr-CA`, `zh-Hant-TW`
//     (stored as `zh-TW`: the Region dropdown's ids are language-region pairs);
//   * a bare first language takes the region of a later entry **in the same
//     language** — `['en', 'en-GB']` is British — but never another
//     language's: `['ko', 'en-US']` is someone reading Korean first, not an
//     American;
//   * failing both, the language's likely region from CLDR (`Intl.Locale`'s
//     `maximize()`): `ko` is Korea, `ja` Japan, and `en` the United States;
//   * and a region that is not a country (`es-419`, Latin America as a whole)
//     or no usable tag at all is `FALLBACK_LOCALE` — there is no one currency
//     for 419, and guessing one is the error FALLBACK_CURRENCY above exists
//     to avoid.
//
// A country outside `CURRENCY_BY_REGION` is still stored as the browser said
// it: its dates are right, and `deriveLocale` flags the currency as the
// fallback, where `en-US` would flag nothing.
//
// Plain JavaScript rather than the core: the rule is which tag a browser
// offers, and only the web signs a studio up.
//
// Whether MMS's sign-up asks for a country, and what Region a new account
// shows, is not known (#487).
const COUNTRY = /^[A-Z]{2}$/;

function parseTag(tag) {
  try {
    return new Intl.Locale(String(tag || '').replace(/_/g, '-').trim());
  } catch {
    return null;
  }
}

export function signupLocale(languages) {
  const tags = (Array.isArray(languages) ? languages : [languages])
    .map(parseTag)
    .filter((l) => l && l.language && l.language !== 'und');
  const first = tags[0];
  if (!first) return FALLBACK_LOCALE;
  let region = first.region
    || tags.slice(1).find((l) => l.language === first.language && l.region)?.region;
  if (!region) {
    try { region = first.maximize().region; } catch { region = null; }
  }
  if (!region || !COUNTRY.test(region)) return FALLBACK_LOCALE;
  return `${first.language}-${region}`;
}
